/**
 * WelcomePage – pre-auth landing.
 *
 * Warm hero, short value prop, "Get started" + "Sign in" buttons. Shown
 * only to signed-out users. Hero image rotates every ~5s through a roster
 * of diverse learners so newcomers see someone who looks like them.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  MicrophoneStage,
  ChatCircleDots,
  Trophy,
  Compass,
} from '@phosphor-icons/react';

// Rotating roster of Unsplash photos. Kept under /public via direct hotlink
// (Unsplash's CDN is free for non-abusive hotlinking). Each caption stays
// neutral so every image fits the same line of marketing copy.
const HERO_SLIDES = [
  {
    url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    alt: 'Woman smiling while learning a new language',
    caption: 'Warm. Patient. Always listening.',
  },
  {
    url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    alt: 'Man with headphones practising conversation',
    caption: 'Talk from day one — at your pace.',
  },
  {
    url: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    alt: 'Young woman in a scarf practising out loud',
    caption: 'Real conversations, not worksheets.',
  },
  {
    url: 'https://images.unsplash.com/photo-1521119989659-a83eee488004?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    alt: 'Learner with glasses laughing during a chat',
    caption: 'Make mistakes. Laugh. Get better.',
  },
  {
    url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=900&q=80',
    alt: 'Person smiling warmly after a good chat',
    caption: 'Feel yourself improve, week by week.',
  },
];

export default function WelcomePage() {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  // Honor the OS-level "Reduce motion" preference (WCAG 2.3.3) — when set,
  // we never auto-rotate; users can still tap a dot manually.
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (paused || prefersReducedMotion) return undefined;
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % HERO_SLIDES.length);
    }, 5200);
    return () => clearInterval(id);
  }, [paused, prefersReducedMotion]);

  const current = HERO_SLIDES[slide];

  return (
    <div className="lf-shell flex flex-col px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-8 inline-flex items-center gap-2 lf-chip" data-testid="welcome-wordmark">
          <span className="h-2 w-2 rounded-full bg-[var(--lf-primary)]" />
          LingoFlow
        </div>

        <h1 className="text-5xl font-bold tracking-tight leading-[1.05]">
          Speak a new <span className="text-[var(--lf-primary)]">language</span>, not just study one.
        </h1>
        <p className="mt-4 text-lg text-[var(--lf-text-muted)] leading-relaxed">
          Real conversations in English or Spanish with a patient AI partner.
          Start from scratch or jump in — we meet you where you are.
        </p>

        {/* Rotating hero — pauses on hover/focus per WCAG 2.2.2
            ("moving content over 5s must have a pause control"). The
            visible dots double as click targets, satisfying the user-
            controlled rotation requirement. */}
        <div
          className="relative mt-10 h-52 overflow-hidden rounded-3xl border border-[var(--lf-border)]"
          data-testid="welcome-hero-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          role="region"
          aria-label="Hero image carousel — hover to pause"
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={current.url}
              src={current.url}
              alt={current.alt}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.04 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-[#2d2a26cc] via-transparent" />
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <div className="text-xs uppercase tracking-widest opacity-80">
              Your new conversation partner
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={current.caption}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="text-lg font-semibold"
                data-testid="welcome-hero-caption"
              >
                {current.caption}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Carousel dots */}
          <div
            className="absolute bottom-3 right-4 flex items-center gap-1.5"
            data-testid="welcome-hero-dots"
            aria-label="Hero image carousel"
          >
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                aria-label={`Show slide ${i + 1} of ${HERO_SLIDES.length}`}
                aria-current={i === slide ? 'true' : undefined}
                className="grid h-6 w-6 place-items-center"
                data-testid={`welcome-hero-dot-${i}`}
              >
                {/* The visible dot is small for design, but the tappable
                    area above wraps it to ≥24×24px (WCAG 2.5.5 / 2.5.8). */}
                <span
                  aria-hidden="true"
                  className={`h-1.5 rounded-full transition-all ${
                    i === slide
                      ? 'w-6 bg-white'
                      : 'w-1.5 bg-white/50'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          <Feature icon={<MicrophoneStage size={20} weight="duotone" />} title="Voice-first practice" copy="Tap to talk, not type. Build real speaking confidence." />
          <Feature icon={<ChatCircleDots size={20} weight="duotone" />} title="Guided scenarios" copy="Order coffee, do an interview, catch up with a friend." />
          <Feature icon={<Trophy size={20} weight="duotone" />} title="Friendly feedback" copy="Gentle corrections and personalized next steps after every chat." />
        </div>

        <div className="mt-10 space-y-3">
          {/* "See how it works" — promoted to FIRST position in the stack so
              cold/unsure visitors get a low-commitment entry point before
              the "Get started" account-creation funnel. Visual styling is
              kept as an outlined pill (not the filled primary) so eye
              hierarchy still flows: low-friction tour → primary CTA →
              returning-user link. The "· 60 sec tour" hint reduces drop-off
              vs. a vague "Learn more" label. */}
          <Link
            to="/guide"
            className="flex w-full items-center justify-center gap-2 rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] px-5 py-3.5 text-base text-[var(--lf-text)] transition-colors hover:border-[var(--lf-primary)] hover:bg-[#FFF5EE]"
            data-testid="welcome-guide-link"
            aria-label="See how LingoFlow works — 60 second tour"
          >
            <Compass size={18} weight="duotone" className="text-[var(--lf-primary)]" />
            <span className="font-semibold">See how it works</span>
            <span className="text-xs text-[var(--lf-text-muted)]">· 60 sec tour</span>
          </Link>

          <Link to="/register" className="lf-btn lf-btn-primary w-full" data-testid="welcome-get-started-btn">
            Get started
          </Link>

          <Link to="/login" className="lf-btn lf-btn-ghost w-full" data-testid="welcome-signin-btn">
            I already have an account
          </Link>
        </div>

        {/* Compliance footer — intentionally low visual weight. Required by
            both ADA case law (a published accessibility statement is a
            material defensibility signal) and Google Play's review process
            (which expects a privacy URL on the public landing surface).

            `lf-bottom-safe` keeps these links clear of the Emergent badge
            in the bottom-right of the viewport. */}
        <footer className="mt-10 flex items-center justify-center gap-4 text-xs text-[var(--lf-text-muted)] lf-bottom-safe">
          <Link to="/privacy" className="underline-offset-2 hover:underline" data-testid="welcome-privacy-link">
            Privacy
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/accessibility" className="underline-offset-2 hover:underline" data-testid="welcome-accessibility-link">
            Accessibility
          </Link>
        </footer>
      </motion.div>
    </div>
  );
}

function Feature({ icon, title, copy }) {
  return (
    <div className="lf-card flex items-start gap-3 p-4">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]">
        {icon}
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-[var(--lf-text-muted)]">{copy}</div>
      </div>
    </div>
  );
}
